// @flow

import {each, includes, uniq, flatten} from "lodash"
import type {
  ShopState,
  ShopItem,
  SearchItem,
  ShopItemType,
  Action,
} from "../types"

const initialState: ShopState = {
  shopItems: [],
  searchItems: [],
  selectedShopItems: [],
  selectedSearchItems: [],
  shopItemMedia: {
    babyMedia: [],
    shopItemMedia: [],
  },
  shop: {isClosed: false, isPublished: false},
  isProcessing: false,
}

const shop = (state: ShopState = initialState, action: Action) => {
  switch (action.type) {
    case "SET_SHOP_ITEMS":
      return {
        ...state,
        shopItems: action.shopItems,
      }
    case "SET_SHOP":
      return {
        ...state,
        shop: action.shop,
      }
    case "UNSELECT_SHOP_ITEMS":
      return {
        ...state,
        selectedShopItems: [],
      }
    case "SET_SHOP_SEARCH_ITEMS":
      return {
        ...state,
        searchItems: action.searchItems,
      }
    case "TOGGLE_SELECTED_SHOP_ITEM":
      const id = action.shopItemId
      return {
        ...state,
        selectedShopItems: ((): $ReadOnlyArray<number> => {
          if (includes(state.selectedShopItems, id)) {
            return state.selectedShopItems.filter((i) => i !== id)
          }
          return [...state.selectedShopItems, id]
        })(),
      }
    case "TOGGLE_SHOP_SELECTED_SEARCH_ITEM":
      const key = action.searchItemKey
      return {
        ...state,
        selectedSearchItems: ((): $ReadOnlyArray<number> => {
          if (includes(state.selectedSearchItems, key)) {
            return state.selectedSearchItems.filter((i) => i !== key)
          }
          return [...state.selectedSearchItems, key]
        })(),
      }
    case "SET_SHOP_ITEM_MEDIA":
      return {
        ...state,
        shopItemMedia: {
          babyMedia: action.babyMedia,
          shopItemMedia: action.shopItemMedia,
        },
      }
    case "CLEAR_SHOP_ITEM_MEDIA":
      return {
        ...state,
        shopItemMedia: {
          babyMedia: [],
          shopItemMedia: [],
        },
      }
    case "SELECT_ALL_SHOP_ITEMS":
      return {
        ...state,
        selectedShopItems: ((): $ReadOnlyArray<number> =>
          state.shopItems.map((s) => s.id))(),
      }
    case "CLEAR_SELECTED_SEARCH_ITEMS":
      return {
        ...state,
        selectedSearchItems: [],
      }
    default:
      return state
  }
}

export default shop

export const isActionAvailable = (
  searchItems: $ReadOnlyArray<SearchItem>,
  selectedSearchItems: $ReadOnlyArray<string>,
  shopItems: $ReadOnlyArray<ShopItem>,
  type: ShopItemType
): boolean => {
  const l = selectedSearchItems.length
  if (l == 0) return false

  const withShopItemsInShop = areProductsWithShopItemsInShopAsProduct(
    shopItems,
    searchItems,
    selectedSearchItems
  )

  const withShopProductItemsInShop = areShopProductsInShopForProduct(
    shopItems,
    searchItems,
    selectedSearchItems
  )

  // when passing in items singles, there should be NO sets
  // under no circumstance should there be more than one set
  const setsOk =
    l > 1 && isAnySearchItemSetSelected(searchItems, selectedSearchItems)
      ? false
      : true
  switch (type) {
    case "single":
      if (withShopProductItemsInShop) {
        return false
      }
      if (l > 1 && setsOk) {
        return true
      }
      if (l == 1) {
        return !isSearchItemSet(selectedSearchItems[0], searchItems)
      }
    case "group":
      if (withShopProductItemsInShop) {
        return false
      }
      if (l == 1) {
        return isSearchItemSet(selectedSearchItems[0], searchItems)
      }
      return setsOk
    case "multi":
      if (withShopProductItemsInShop) {
        return false
      }
      return (
        l > 1 &&
        allItemsAreSameBabyListItem(searchItems, selectedSearchItems) &&
        setsOk
      )
    case "shop-product":
      const searchItem = getSearchItemByKey(selectedSearchItems[0], searchItems)
      if (
        l == 1 &&
        searchItem &&
        searchItem.shopProducts.length > 0 &&
        !withShopItemsInShop
      ) {
        // only if still available
        // console.log(searchItem)
        return true
      }
    default:
      return false
  }
}

const areShopProductsInShopForProduct = (
  shopItems: $ReadOnlyArray<SearchItem>,
  searchItems: $ReadOnlyArray<SearchItem>,
  selectedSearchItems: $ReadOnlyArray<string>
): boolean => {
  const allOnList = flatten(
    shopItems
      .filter(({shopProductId}) => shopProductId != null)
      .map(({babyListItems}) => babyListItems)
  ).map(({id}) => id)
  const onList = selectedSearchItems.filter((item) => {
    const searchItem = getSearchItemByKey(item, searchItems)
    if (!searchItem) {
      return false
    }
    return includes(allOnList, searchItem.id)
  })
  return onList.length > 0
}

const areProductsWithShopItemsInShopAsProduct = (
  shopItems: $ReadOnlyArray<SearchItem>,
  searchItems: $ReadOnlyArray<SearchItem>,
  selectedSearchItems: $ReadOnlyArray<string>
): boolean => {
  const allOnList = flatten(
    shopItems
      .filter(({shopProductId}) => shopProductId == null)
      .map(({babyListItems}) => babyListItems)
  ).map(({id}) => id)
  const onList = selectedSearchItems.filter((item) => {
    const searchItem = getSearchItemByKey(item, searchItems)
    if (!searchItem) {
      return false
    }
    return includes(allOnList, searchItem.id)
  })
  return onList.length > 0
}

const allItemsAreSameBabyListItem = (
  searchItems: $ReadOnlyArray<SearchItem>,
  selectedSearchItems: $ReadOnlyArray<string>
): boolean => {
  const searchItemIdsToSell = searchItems
    .filter((i) => includes(selectedSearchItems, i.key))
    .map((i) => i.id)
  return uniq(searchItemIdsToSell).length == 1
}

export const isShopItemSelected = (
  shopItemId: number,
  selectedShopItems: $ReadOnlyArray<number>
): boolean => includes(selectedShopItems, shopItemId)

export const isSearchItemSubSelected = (
  searchItemKey: string,
  searchItems: $ReadOnlyArray<SearchItem>,
  selectedSearchItems: $ReadOnlyArray<string>
): boolean => {
  const searchItem = getSearchItemByKey(searchItemKey, searchItems)
  if (!searchItem || !searchItem.isSet) {
    return false
  }
  return (
    searchItem.items &&
    searchItem.items.filter((item) => includes(selectedSearchItems, item.key))
      .length > 0
  )
}

const isSearchItemSet = (
  searchItemKey: string,
  searchItems: $ReadOnlyArray<SearchItem>
): boolean => {
  const item = getSearchItemByKey(searchItemKey, searchItems)
  if (!item) {
    return false
  }
  if (item && item.isSet) {
    return true
  }
  return false
}

export const getSearchItemByKey = (
  searchItemKey: string,
  searchItems: $ReadOnlyArray<SearchItem>
): ?SearchItem => {
  let searchItem = null
  each(searchItems, (item) => {
    if (item.key == searchItemKey) {
      searchItem = item
      return false // stop
    }
    if (item.items) {
      each(item.items, (subItem) => {
        if (subItem.key == searchItemKey) {
          searchItem = subItem
          return false // stop
        }
      })
    }
  })
  return searchItem
}

const isSearchItemSelected = (
  searchItemKey: string,
  selectedSearchItems: $ReadOnlyArray<string>
): boolean => includes(selectedSearchItems, searchItemKey)

export const getSelectSearchItemIds = (
  selectedSearchItems: $ReadOnlyArray<string>,
  searchItems: $ReadOnlyArray<SearchItem>
): $ReadOnlyArray<number> => {
  return searchItems.reduce((acc, item) => {
    if (isSearchItemSelected(item.key, selectedSearchItems)) {
      return [...acc, item.id]
    }
    if (item.items) {
      return item.items.reduce((subAcc, subItem) => {
        return isSearchItemSelected(subItem.key, selectedSearchItems)
          ? [...subAcc, subItem.id]
          : subAcc
      }, acc)
    }
    return acc
  }, [])
}

export const isAnySearchItemSetSelected = (
  searchItems: $ReadOnlyArray<SearchItem>,
  selectedSearchItems: $ReadOnlyArray<string>
): boolean =>
  searchItems
    .filter(({key}) => isSearchItemSelected(key, selectedSearchItems))
    .some((item) => item.isSet)

export const isAnySearchItemSetSelectedExcluding = (
  searchItems: $ReadOnlyArray<SearchItem>,
  selectedSearchItems: $ReadOnlyArray<string>,
  excludedKey: string
): boolean =>
  searchItems
    .filter(({key}) => isSearchItemSelected(key, selectedSearchItems))
    .filter((item) => item.key !== excludedKey)
    .some((item) => item.isSet)
